import { useContext, useEffect, useMemo, useRef } from "react";
import { Provider } from "react-redux";
import { store, useAppDispatch, useAppSelector } from "store/store";
import { createRoot } from "react-dom/client";
import ChatWrapper from "components/Chat/ChatWrapper";
import { GlobalAppState } from "globals/interfaces";
import TranslationProvider from "providers/translation-provider";
import useRegister from "hooks/useRegister";
import DraggedDiv from "components/layouts/Dragable";
import { MediaContext } from "context/MediaContext";
import { useCreatePingQuery } from "services/ping";
import { pingInterval } from "config/constant";
import { MediaStreamErrorNames, SessionStatus } from "globals/enums";
import { ProctoringRoute, getEndpoint } from "routes/route";
import { showProview } from "helpers/showProview";
import { pauseProctor, resumeProctor } from "helpers/pauseProctor";
import { useNavigate } from "react-router-dom";
import { AlertName } from "helpers/alert-type";
import { isTsbBrowser } from "helpers/browser-helper";
import { setSessionStatus } from "store/app.slice";
import { resetNotificationCounters } from "helpers/notify-candidate";
import { isRestrictedTriggerEvent } from "helpers/proctor-client-trigger-helper";
import { getInitialData } from "helpers/common";

const Shell = () => {
  const globalState: GlobalAppState = useAppSelector((state) => state.app);
  const sessionStatus = useAppSelector((state) => state.app.sessionStatus);
  const sessionDetails = useAppSelector((state: any) => state.app.session);
  const { mediaStreamError } = useContext(MediaContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const envDetails = useMemo(
    () => getInitialData(window?.tv?.q?.[0], globalState.initScriptConfiguration),
    [globalState.initScriptConfiguration]
  );
  const shouldRegisterHooks = !envDetails.clientConfiguration.enforceTSB || isTsbBrowser();
  
  useRegister(shouldRegisterHooks);

  useCreatePingQuery(
    { session_token: sessionDetails?.session_token },
    {
      skip: !sessionDetails?.session_token || globalState.sessionStatus === "completed",
      pollingInterval: pingInterval
    }
  );

  const lastSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionDetails?.session_id) return;
    if (lastSessionIdRef.current !== sessionDetails.session_id) {
      lastSessionIdRef.current = sessionDetails.session_id;
      resetNotificationCounters();
    }
  }, [sessionDetails?.session_id]);

  useEffect(() => {
    if (mediaStreamError) {
      if (mediaStreamError?.name === MediaStreamErrorNames.TrackFailedError) {
        navigate(getEndpoint(ProctoringRoute.Error));
      } else if (mediaStreamError.name === MediaStreamErrorNames.AudioHardwareFailedError) {
        showProview();
        navigate(getEndpoint(ProctoringRoute.HardwareTestInstructions));
      } else if (mediaStreamError.name === MediaStreamErrorNames.VideoHardwareFailedError) {
        showProview();
        navigate(getEndpoint(ProctoringRoute.FaceCaptureInstructions));
      }
    }
  }, [mediaStreamError]);

  useEffect(() => {
    const handleTermination = async () => {
      clearInterval(window.pingIntervalId as any);
      const draggableComponent = document.getElementById("tvp_recorder_holder");
      draggableComponent.style.display = "none";
      const terminationData = { alert_type_id: 144, description: AlertName.SessionTerminated };
      "log:event:all log:event:type:144".split(" ").forEach((eventName) => {
        window.ProctorClient3.eventHandlers?.[eventName]?.forEach((handler: any) => handler(terminationData));
      });
    };
    document.addEventListener("Terminated", handleTermination);

    const previousClient = window.ProctorClient3 || {};
    const previousEventHandlers = previousClient.eventHandlers || {};
    const previousFinalCallback = previousClient.finalCallBack;

    window.ProctorClient3 = {
      ...previousClient,
      eventHandlers: previousEventHandlers,
      stop: (finalCallBack: any) => {
        window.ProctorClient3.finalCallBack = finalCallBack;
        const event = new CustomEvent("StopProctorClient");
        document.dispatchEvent(event);
        const videoTracks = new CustomEvent("StopVideoTracks");
        document.dispatchEvent(videoTracks);
        clearInterval(window.pingIntervalId as any);
        if (sessionStatus !== SessionStatus.Terminated) {
          dispatch(setSessionStatus({ status: SessionStatus.Completed }));
        }
      },
      pause: (code: string, reason?: string, note?: string) => {
        pauseProctor(dispatch, navigate, code, reason, note);
      },
      resume: () => {
        resumeProctor(dispatch);
      },
      giveControl: () => {
        if (window?.tv) {
          return window?.tv.q[0][2];
        }
        return null;
      },
      networkDisconnectionCallback: () => {
        if (window?.tv && typeof window.tv.q[0][2]?.networkDisconnectionCallback === "function") {
          return window?.tv.q[0][2]?.networkDisconnectionCallback();
        }
      },
      trigger(eventNames: string, data: any) {
        if (isRestrictedTriggerEvent(eventNames, data)) {
          return;
        }
        const eventNameList = eventNames.split(" ");
        if (eventNameList && eventNameList.length > 0) {
          eventNameList.forEach((eventName) => {
            if (this.eventHandlers[eventName]) {
              this.eventHandlers[eventName].forEach((handler) => {
                handler(data);
              });
            }
          });
        }
      },
      on(eventName: string, callback) {
        if (!this.eventHandlers[eventName]) {
          this.eventHandlers[eventName] = [];
        }
        this.eventHandlers[eventName].push(callback);
      },
      isPhotoIdFailed:
        typeof previousClient.isPhotoIdFailed === "boolean" ? previousClient.isPhotoIdFailed : false,
      isTextEnvFailed:
        typeof previousClient.isTextEnvFailed === "boolean"
          ? previousClient.isTextEnvFailed
          : false
    };
    if (previousFinalCallback) {
      window.ProctorClient3.finalCallBack = previousFinalCallback;
    }
    return () => {
      document.removeEventListener("Terminated", handleTermination);
    };
  }, [dispatch, navigate, sessionStatus]);

  const createVideoHolderRoot = () => {
    const container = document.createElement("div");
    container.className = "tvp-proview_root";
    const root = createRoot(container);
    root.render(
      <Provider store={store}>
        <TranslationProvider>
          <DraggedDiv />
        </TranslationProvider>
      </Provider>
    );
    document.body.appendChild(container);
  };

  useEffect(() => {
    createVideoHolderRoot();
  }, []);

  const handleAppendChat = () => {
    const chatContainer = document.getElementById("tvp-chat-container");
    if (chatContainer) {
      const commentRoot = createRoot(chatContainer);
      commentRoot.render(
        <Provider store={store}>
          <ChatWrapper />
        </Provider>
      );
    }
  };

  useEffect(() => {
    globalState.chatService && handleAppendChat();
  }, [globalState.chatService]);

  return <></>;
};

export default Shell;
